import * as config from "../util/config"

import {
  StoryblokComponent,
  apiPlugin,
  getStoryblokApi,
  storyblokInit,
  useStoryblokState,
} from "@storyblok/react"

import PageHead from "../components/head/PageHead"
import React from "react"
import ReviewsContextProvider from "../context/ReviewsContext/ReviewsContextProvider"
import { ServerResponse } from "http"
import components from "../storyblok"
import { env } from "../config"

storyblokInit({
  accessToken: env("STORYBLOK_API_KEY"),
  use: [apiPlugin],
  apiOptions: {
    region: "us",
  },
  components,
})

const REDIRECT_PATHS = ["shipments", "new", "login", "register", "password"]

const shouldRedirect = (slug: string[]): boolean => {
  return slug.some((segment) => REDIRECT_PATHS.includes(segment))
}

const handleRedirect = (res: ServerResponse, slug: string[]): void => {
  const host = config.getAppHost()

  const redirectURL = `${host}/${slug.join("/")}`
  res.writeHead(301, { Location: redirectURL })
  res.end()
}

type Props = {
  story?: any
  preview?: boolean
  key?: number | string
}

const Page: React.FC<Props> = ({ story, preview }) => {
  story = useStoryblokState(story, {})
  if (!story) return null
  const { content } = story

  return (
    <ReviewsContextProvider preview={preview}>
      <PageHead
        title={content.title}
        description={content.description}
        customScript={content.customScript}
      >
        {content.richSnippets?.map((item: { content: any }, n: number) => (
          <script
            key={`application/ld+json${n}`}
            id={`application/ld+json${n}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: item.content }}
          />
        ))}
      </PageHead>
      <StoryblokComponent blok={content} />
    </ReviewsContextProvider>
  )
}

export default Page

type ServerSidePropsContext = {
  params: { slug: string[] }
  preview: boolean
  res: ServerResponse
  query: { [key: string]: string }
}

async function fetchData(slug: string) {
  const sbParams = {
    version:
      env("STORYBLOK_ENV") === "published"
        ? ("published" as const)
        : ("draft" as const),
    token: env("STORYBLOK_API_KEY"),
  }
  const storyblokApi = getStoryblokApi()
  const { data } = await storyblokApi.get(`cdn/stories/${slug}`, sbParams)

  const { story } = data || {}
  const { id } = story || {}

  return {
    story: story || null,
    key: id || null,
  }
}
export async function getServerSideProps({
  params,
  preview,
  res,
  query,
}: ServerSidePropsContext) {
  try {
    if (shouldRedirect(params.slug)) {
      //TODO: remove  redirect logic if handled by nginx
      handleRedirect(res, params.slug)
      return { props: {} }
    }

    const slug = params.slug ? params.slug.join("/") : "home"

    if (slug.includes(".map") || slug.includes("_next/")) {
      return { props: {} }
    }

    const result = await fetchData(slug)

    const { story, key } = result
    return {
      props: {
        story,
        key,
        preview: preview || false,
      },
    }
  } catch (e) {
    console.warn("Error fetching story:", e)
    return {
      notFound: true,
    }
  }
}
